import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Calendar,
  Plus,
  Trash2,
  Edit2,
  CalendarOff,
  CalendarDays,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

const HolidayManagementPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [holidays, setHolidays] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState(null);
  const [editingHoliday, setEditingHoliday] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    date: '',
    name: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchHolidays = useCallback(async () => {
    try {
      const response = await api.get(`/holidays?year=${selectedYear}`);
      setHolidays(response.data.holidays || []);
      setStats({
        currentMonthHolidays: response.data.current_month_holidays,
        currentMonthWorkingDays: response.data.current_month_working_days,
        elapsedWorkingDays: response.data.elapsed_working_days
      });
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, [api, selectedYear]);

  useEffect(() => {
    fetchHolidays();
  }, [fetchHolidays]);

  const handleAddHoliday = async () => {
    if (!formData.date || !formData.name.trim()) {
      toast.error('Please enter date and name');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/holidays', {
        date: formData.date,
        name: formData.name.trim(),
        description: formData.description.trim() || null
      });
      toast.success('Holiday added successfully');
      setShowAddDialog(false);
      setFormData({ date: '', name: '', description: '' });
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add holiday');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateHoliday = async () => {
    if (!editingHoliday) return;
    
    setSubmitting(true);
    try {
      await api.put(`/holidays/${editingHoliday.id}`, {
        name: formData.name.trim(),
        description: formData.description.trim() || null
      });
      toast.success('Holiday updated');
      setShowEditDialog(false);
      setEditingHoliday(null);
      setFormData({ date: '', name: '', description: '' });
      fetchHolidays();
    } catch (error) {
      toast.error('Failed to update holiday');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteHoliday = async () => {
    if (!holidayToDelete) return;
    
    try {
      await api.delete(`/holidays/${holidayToDelete.id}`);
      toast.success('Holiday removed');
      setHolidayToDelete(null);
      fetchHolidays();
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };

  const openEditDialog = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      date: holiday.date,
      name: holiday.name,
      description: holiday.description || ''
    });
    setShowEditDialog(true);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isPastDate = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr + 'T00:00:00');
    return date < today;
  };

  const isToday = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  // Group holidays by month
  const holidaysByMonth = holidays.reduce((acc, holiday) => {
    const month = holiday.date.substring(0, 7); // YYYY-MM
    if (!acc[month]) acc[month] = [];
    acc[month].push(holiday);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="holiday-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarOff className="w-7 h-7 text-red-500" />
            Holiday Management
          </h1>
          <p className="text-slate-600 mt-1">
            Manage holidays to exclude them from working day calculations
          </p>
        </div>
        <Button
          onClick={() => {
            setFormData({ date: '', name: '', description: '' });
            setShowAddDialog(true);
          }}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="add-holiday-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Holiday
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <CalendarOff className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.currentMonthHolidays || 0}</p>
                <p className="text-sm text-slate-500">Holidays This Month</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CalendarDays className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.currentMonthWorkingDays || 0}</p>
                <p className="text-sm text-slate-500">Total Working Days</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.elapsedWorkingDays || 0}</p>
                <p className="text-sm text-slate-500">Working Days Elapsed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">How holidays affect calculations:</p>
          <ul className="mt-1 list-disc list-inside space-y-1 text-blue-700">
            <li>Holidays are excluded from working day count</li>
            <li>Average sales per day is calculated using actual working days only</li>
            <li>Charts and graphs will not count holidays in working day metrics</li>
            <li>Weekends (Sat/Sun) are already excluded - only add weekday holidays</li>
          </ul>
        </div>
      </div>

      {/* Year Selector */}
      <div className="flex items-center gap-2">
        <Label>Year:</Label>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          data-testid="year-selector"
        >
          {[2025, 2026, 2027].map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <Button variant="outline" size="sm" onClick={fetchHolidays}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Holidays List */}
      <Card className="border-0 shadow-xl">
        <CardHeader className="bg-gradient-to-r from-red-500 to-orange-500 text-white">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Holidays in {selectedYear}
            <span className="ml-2 text-sm font-normal text-white/80">
              ({holidays.length} total)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {holidays.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {Object.entries(holidaysByMonth).map(([month, monthHolidays]) => (
                <div key={month}>
                  <div className="px-4 py-2 bg-slate-50 text-sm font-medium text-slate-600">
                    {new Date(month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </div>
                  {monthHolidays.map((holiday) => (
                    <div 
                      key={holiday.id}
                      className={`flex items-center justify-between p-4 hover:bg-slate-50 transition-colors ${
                        isPastDate(holiday.date) ? 'opacity-60' : ''
                      } ${isToday(holiday.date) ? 'bg-amber-50' : ''}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          isPastDate(holiday.date) ? 'bg-slate-100' : 'bg-red-100'
                        }`}>
                          <CalendarOff className={`w-6 h-6 ${
                            isPastDate(holiday.date) ? 'text-slate-400' : 'text-red-500'
                          }`} />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{holiday.name}</p>
                          <p className="text-sm text-slate-500">
                            {formatDate(holiday.date)}
                            {holiday.day_of_week && ` (${holiday.day_of_week})`}
                          </p>
                          {holiday.description && (
                            <p className="text-xs text-slate-400 mt-0.5">{holiday.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isToday(holiday.date) && (
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                            Today
                          </span>
                        )}
                        {isPastDate(holiday.date) && !isToday(holiday.date) && (
                          <span className="px-2 py-1 bg-slate-100 text-slate-500 rounded-full text-xs">
                            Past
                          </span>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(holiday)}
                          className="h-8 px-2"
                          data-testid={`edit-holiday-${holiday.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setHolidayToDelete(holiday)}
                          className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-holiday-${holiday.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center text-slate-500">
              <CalendarOff className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">No holidays added for {selectedYear}</p>
              <p className="text-sm mt-1">Click "Add Holiday" to add your first holiday</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Holiday Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-indigo-600" />
              Add Holiday
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input 
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                data-testid="holiday-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Holiday Name *</Label>
              <Input 
                placeholder="e.g., Independence Day, Christmas"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                data-testid="holiday-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input 
                placeholder="Any additional notes..."
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                data-testid="holiday-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddHoliday} disabled={submitting} data-testid="save-holiday-btn">
              {submitting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Holiday
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Holiday Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-600" />
              Edit Holiday
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input 
                type="date"
                value={formData.date}
                disabled
                className="bg-slate-50"
              />
              <p className="text-xs text-slate-500">Date cannot be changed. Delete and recreate if needed.</p>
            </div>
            <div className="space-y-2">
              <Label>Holiday Name *</Label>
              <Input 
                placeholder="e.g., Independence Day"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input 
                placeholder="Any additional notes..."
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateHoliday} disabled={submitting}>
              {submitting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!holidayToDelete} onOpenChange={() => setHolidayToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Delete Holiday
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{holidayToDelete?.name}" ({holidayToDelete?.date})?
              This will make that date count as a working day again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteHoliday} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HolidayManagementPage;
